import { ref, computed } from 'vue'
import { ElNotification } from 'element-plus'
import geocodingService from '@/api/geocodingService'
import request from '@/utils/request'

// État global partagé
const isRunning = ref(false)
const currentIndex = ref(0)
const totalCount = ref(0)
const geocodedResults = ref([])
const clientsToGeocode = ref([])

// Buffer pour les coordonnées à sauvegarder par lot
const coordsBuffer = ref([])
const BATCH_SIZE = 100

// Pour annuler le géocodage
let abortController = null

/**
 * Composable pour gérer le géocodage en arrière-plan
 */
export function useGeocoding() {
  const progress = computed(() => {
    if (totalCount.value === 0) return 0
    return Math.round((currentIndex.value / totalCount.value) * 100)
  })

  const statusText = computed(() => {
    if (!isRunning.value) return ''
    return `Géocodage: ${currentIndex.value}/${totalCount.value}`
  })

  /**
   * Démarre le géocodage en arrière-plan
   */
  const startGeocoding = async (clients, onComplete = null) => {
    if (isRunning.value) {
      ElNotification({
        title: 'Géocodage en cours',
        message: 'Un géocodage est déjà en cours. Veuillez patienter.',
        type: 'warning',
        duration: 3000,
      })
      return false
    }

    // Filtrer les clients sans coordonnées
    const toGeocode = clients.filter(c => !c.lat || !c.lon)

    if (toGeocode.length === 0) {
      ElNotification({
        title: 'Géocodage',
        message: 'Tous les clients ont déjà des coordonnées GPS.',
        type: 'info',
        duration: 3000,
      })
      return false
    }

    // Initialiser l'état
    isRunning.value = true
    currentIndex.value = 0
    totalCount.value = toGeocode.length
    geocodedResults.value = []
    clientsToGeocode.value = toGeocode
    abortController = { cancelled: false }

    // Notification de démarrage
    ElNotification({
      title: 'Géocodage lancé',
      message: `${toGeocode.length} client(s) à géolocaliser. Vous pouvez continuer à naviguer.`,
      type: 'info',
      duration: 4000,
    })

    // Lancer le géocodage en arrière-plan
    processGeocoding(onComplete)

    return true
  }

  /**
   * Sauvegarde un lot de coordonnées vers l'API
   */
  const saveBatch = async (coords) => {
    if (coords.length === 0) return

    try {
      await request({
        url: '/api/comptes/update-coords',
        method: 'put',
        data: { coords }
      })
      console.log(`Lot de ${coords.length} coordonnées sauvegardé`)
    } catch (error) {
      console.error('Erreur sauvegarde lot de coordonnées:', error)
    }
  }

  /**
   * Traite le géocodage de manière asynchrone
   */
  const processGeocoding = async (onComplete) => {
    const results = []
    let successCount = 0
    let errorCount = 0
    coordsBuffer.value = []

    for (let i = 0; i < clientsToGeocode.value.length; i++) {
      // Vérifier si annulé
      if (abortController?.cancelled) {
        break
      }

      const client = clientsToGeocode.value[i]
      currentIndex.value = i + 1

      try {
        const result = await geocodingService.geocodeClient(client)

        if (result) {
          results.push(result)
          geocodedResults.value.push(result)
          successCount++

          // Ajouter au buffer
          coordsBuffer.value.push({
            id: result.id,
            lat: result.lat,
            lon: result.lon
          })

          // Sauvegarder par lot de BATCH_SIZE
          if (coordsBuffer.value.length >= BATCH_SIZE) {
            await saveBatch([...coordsBuffer.value])
            coordsBuffer.value = []
          }
        } else {
          errorCount++
        }
      } catch (error) {
        console.error(`Erreur géocodage client ${client.id}:`, error)
        errorCount++
      }

      // Petit délai pour ne pas surcharger l'API de géocodage
      await new Promise(resolve => setTimeout(resolve, 150))
    }

    // Sauvegarder le reste du buffer
    if (coordsBuffer.value.length > 0) {
      await saveBatch([...coordsBuffer.value])
      coordsBuffer.value = []
    }

    // Terminé
    isRunning.value = false

    // Notification de fin
    if (!abortController?.cancelled) {
      ElNotification({
        title: 'Géocodage terminé',
        message: `${successCount} client(s) géolocalisé(s)${errorCount > 0 ? `, ${errorCount} erreur(s)` : ''}.`,
        type: successCount > 0 ? 'success' : 'warning',
        duration: 5000,
      })
    }

    // Callback de fin
    if (onComplete) {
      onComplete(results)
    }

    return results
  }

  /**
   * Annule le géocodage en cours
   */
  const cancelGeocoding = async () => {
    if (abortController) {
      abortController.cancelled = true
    }

    // Sauvegarder le buffer restant avant d'arrêter
    if (coordsBuffer.value.length > 0) {
      await saveBatch([...coordsBuffer.value])
      coordsBuffer.value = []
    }

    isRunning.value = false

    ElNotification({
      title: 'Géocodage annulé',
      message: `${geocodedResults.value.length} client(s) ont été géolocalisés avant l'annulation.`,
      type: 'info',
      duration: 3000,
    })
  }

  return {
    // État
    isRunning,
    currentIndex,
    totalCount,
    progress,
    statusText,
    geocodedResults,

    // Actions
    startGeocoding,
    cancelGeocoding,
  }
}

export default useGeocoding
